<template>
  <div class="container d-flex flex-column gap-4">
    <div class="row"></div>
    <div class="card">
      <div class="card-body">
        <div class="row">
          <h4>{{ headerTitle }}</h4>
          <DocumetFilters
            v-model="filters"
            @apply-filters="handleApplyFilters"
            :documentTypeId="Number(route.query.documentTypeId)"
            @update:availableYears="availableYears = $event"
          />
        </div>
        <div class="row">
          <div class="mb-3">
            <div v-if="loading" class="text-center">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            <div v-if="error" class="alert alert-danger">
              {{ error }}
            </div>
          </div>
          <table v-if="!loading && !error" class="table table-sm table-hover table-bordered">
            <thead>
              <tr>
                <th colspan="4" class="search-header">
                  <div class="input-group input-group-sm w-25 ms-auto">
                    <input
                      type="text"
                      v-model="searchValue"
                      class="form-control"
                      :placeholder="$t('dokumentet.search_placeholder')"
                    />
                    <span class="input-group-text bg-transparent">
                      <i class="bi bi-search"></i>
                    </span>
                    <button
                      v-if="searchValue"
                      class="btn btn-outline-secondary btn-sm"
                      @click="searchValue = ''"
                    >
                      Clear
                    </button>
                  </div>
                </th>
              </tr>
              <tr>
                <th class="id-column text-center align-middle">#</th>
                <th class="date-column">{{ $t('dokumentet.data') }}</th>
                <th>{{ $t('dokumentet.title') }}</th>
                <th class="text-center align-middle"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(doc, index) in paginatedTree"
                :key="doc.Id"
                :class="[
                  doc.level === 0 ? 'parent-row' : 'child-row',
                  { 'has-parent': doc.level > 0 }
                ]"
              >
                <td class="text-center align-middle">
                  {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                </td>
                <td>{{ doc.CreatedDate }}</td>
                <td :style="{ paddingLeft: doc.level * 24 + 'px' }">
                  <span
                    v-if="doc.isChild || doc.isParent"
                    class="me-2 toggle-icon"
                    @click="toggleExpand(doc)"
                    style="cursor: pointer;"
                  >
                    <font-awesome-icon
                      :icon="expanded[doc.Id] ? 'fa-solid fa-minus' : 'fa-solid fa-plus'"
                    />
                  </span>
                  <span v-if="doc.level > 0" class="child-connector"></span>
                  {{ doc.Title }}
                </td>
                <td class="text-center align-middle">
                  <a
                    v-if="doc.DocumentFileUrl && doc.DocumentFileUrl !== 'No file'"
                    :href="doc.DocumentFileUrl"
                    target="_blank"
                  >
                    <font-awesome-icon icon="fa-solid fa-file-pdf" />
                  </a>
                  <span v-else></span>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="!loading && !error && paginatedTree.length === 0" class="alert alert-info">
            No documents found.
          </div>
          <div class="d-flex justify-content-between align-items-center mb-3 pagination-container">
            <nav aria-label="Page navigation" class="pagination-wrapper">
              <ul class="pagination mb-0">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <button class="page-link" @click="changePage(currentPage - 1)">
                    <font-awesome-icon icon="fa-solid fa-angle-left" />
                  </button>
                </li>
                <li
                  class="page-item"
                  v-for="page in visiblePages"
                  :key="page"
                  :class="{ active: currentPage === page, disabled: page === '...' }"
                >
                  <button class="page-link" @click="goToPage(page)" :disabled="page === '...'">{{ page }}</button>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                  <button class="page-link" @click="changePage(currentPage + 1)">
                    <font-awesome-icon icon="fa-solid fa-angle-right" />
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useDocumentStore } from '@/store/DocumentStore/documentStore';
import { useRoute, useRouter } from 'vue-router';
import DocumetFilters from '../share/DocumetFilters.vue';

export default {
  name: 'DocumentPage',
  setup() {
    const router = useRouter();
    const route = useRoute();
    return { router, route };
  },
  data() {
    return {
      documentStore: useDocumentStore(),
      searchValue: '',
      currentPage: 1,
      itemsPerPage: 15,
      headerTitle: '',
      filters: {
        CategoriesIds: [],
        SubCategoriesIds: [],
        PostCategoriesIds: [],
        selectYear: new Date().getFullYear(),
      },
      expanded: {},
      availableYears: [],
    };
  },
  components: {
    DocumetFilters,
  },
  mounted() {
    this.headerTitle = this.$route.query.title || 'Documents';
    this.currentPage = Number(this.$route.query.page) || 1;
    this.fetchDocuments();
  },
  watch: {
    '$route.query.page': {
      handler(newPage) {
        const pageNum = Number(newPage) || 1;
        if (pageNum >= 1 && pageNum <= this.totalPages) {
          this.currentPage = pageNum;
        } else {
          this.currentPage = 1;
          this.router.push({
            query: { ...this.$route.query, page: 1 },
          });
        }
      },
      immediate: true,
    },
    totalPages(newTotal) {
      if (this.currentPage > newTotal && newTotal > 0) {
        this.currentPage = 1;
        this.router.push({
          query: { ...this.$route.query, page: 1 },
        });
      }
    },
  },
  computed: {
    documentTree() {
      const map = new Map();
      const tree = [];
      this.documentStore.documentet.forEach(doc => {
        map.set(doc.Id, { ...doc, children: [], level: 0 });
      });
      this.documentStore.documentet.forEach(doc => {
        const parent = map.get(doc.ParentId);
        if (doc.ParentId === 0 || !parent) {
          tree.push(map.get(doc.Id));
        } else {
          parent.children.push({ ...map.get(doc.Id), level: parent.level + 1 });
        }
      });
      return tree;
    },
    filteredTree() {
      if (!this.searchValue) return this.documentTree;
      const searchTerm = this.searchValue.toLowerCase();
      const filteredTree = [];
      const filterNode = (node) => {
        const matches =
          node.Title?.toLowerCase().includes(searchTerm) ||
          node.Language?.toLowerCase().includes(searchTerm);
        const filteredNode = { ...node, children: [] };
        node.children.forEach(child => {
          const filteredChild = filterNode(child);
          if (filteredChild) {
            filteredNode.children.push(filteredChild);
          }
        });
        if (matches || filteredNode.children.length > 0) {
          return filteredNode;
        }
        return null;
      };
      this.documentTree.forEach(node => {
        const filteredNode = filterNode(node);
        if (filteredNode) {
          filteredTree.push(filteredNode);
        }
      });
      return filteredTree;
    },
    flattenedTree() {
      const flatten = (nodes, result = []) => {
        nodes.forEach(node => {
          result.push(node);
          if (this.expanded[node.Id] && node.children?.length) {
            flatten(node.children, result);
          }
        });
        return result;
      };
      return flatten(this.filteredTree);
    },
    totalPages() {
      return Math.ceil(this.flattenedTree.length / this.itemsPerPage);
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      if (total <= 4) {
        for (let i = 1; i <= total; i++) pages.push(i);
      } else {
        if (current <= 2) {
          pages.push(1, 2, 3, 4, '...', total);
        } else if (current >= total - 1) {
          pages.push(1, '...', total - 3, total - 2, total - 1, total);
        } else {
          pages.push(1, '...', current - 1, current, current + 1, '...', total);
        }
      }
      return pages;
    },
    paginatedTree() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.flattenedTree.slice(start, end);
    },
    loading() {
      return this.documentStore.loading;
    },
    error() {
      return this.documentStore.error;
    },
  },
  methods: {
    async fetchDocuments() {
      let dateRange = null;
      if (this.filters.selectYear === '') {
        if (this.availableYears && this.availableYears.length > 0) {
          const minYear = Math.min(...this.availableYears);
          const maxYear = Math.max(...this.availableYears);
          dateRange = {
            startDate: new Date(minYear, 0, 1),
            endDate: new Date(maxYear, 11, 31),
          };
        }
      } else if (this.filters.selectYear) {
        dateRange = {
          startDate: new Date(this.filters.selectYear, 0, 1),
          endDate: new Date(this.filters.selectYear, 11, 31),
        };
      }
      const params = {
        prokuroriaId: this.route.query.prokuroriaId
          ? parseInt(this.route.query.prokuroriaId, 10)
          : null,
        documentTypeId: this.route.query.documentTypeId
          ? parseInt(this.route.query.documentTypeId, 10)
          : null,
        CategoriesIds: this.filters.CategoriesIds,
        SubCategoriesIds: this.filters.SubCategoriesIds,
        PostCategoriesIds: this.filters.PostCategoriesIds,
        dateRange,
      };
      await this.documentStore.getDocumentet(params);
      if (this.currentPage > this.totalPages && this.totalPages > 0) {
        this.currentPage = 1;
        this.router.push({
          query: { ...this.$route.query, page: 1 },
        });
      }
    },
    handleApplyFilters(filters) {
      this.filters = filters;
      this.currentPage = 1;
      this.expanded = {};
      this.router.push({
        query: { ...this.$route.query, page: 1 },
      });
      this.fetchDocuments();
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.router.push({
          query: { ...this.$route.query, page },
        });
      }
    },
    goToPage(page) {
      if (typeof page === 'number' && page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        this.router.push({
          query: { ...this.$route.query, page },
        });
      }
    },
    async toggleExpand(doc) {
      const id = doc.Id;
      const parentId = doc.ParentId || 0;
      if (this.expanded[id]) {
        this.expanded[id] = false;
        this.expanded = { ...this.expanded };
        return;
      }
      if (!doc.children || doc.children.length === 0) {
        try {
          const children = await this.documentStore.getDocumentChilde({ parentId: parentId, documentId: id });
          this.addChildrenToNode(this.documentTree, id, children);
        } catch (e) {
          console.error('Error fetching children:', e);
        }
      }
      this.expanded[id] = true;
      this.expanded = { ...this.expanded };
    },
    addChildrenToNode(tree, id, children) {
      for (const node of tree) {
        if (node.Id === id) {
          node.children = (children || []).map(child => ({
            ...child,
            children: [],
            level: (node.level || 0) + 1,
          }));
          return true;
        }
        if (node.children && node.children.length) {
          if (this.addChildrenToNode(node.children, id, children)) return true;
        }
      }
      return false;
    },
  },
};
</script>

<style scoped>
.search-header {
  background-color: #f8f9fa;
  padding: 10px;
  border-bottom: 1px solid #dee2e6;
}
td {
  vertical-align: middle;
}
.id-column {
  width: 50px;
  min-width: 50px;
}
.date-column {
  width: 50px;
  min-width: 50px;
}
.title-column {
  width: 150px;
  min-width: 150px;
}
@media (max-width: 576px) {
  .input-group {
    width: 100% !important;
  }
}
.card {
  background: #eff2f5 !important;
  margin: 0;
  border: transparent !important;
  border-radius: 12px !important;
}
.card-body {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}
.input-group {
  max-width: 300px;
}
.input-group .form-control {
  background-color: transparent;
  box-shadow: none;
}
.input-group .form-control:focus {
  border-color: #ced4da;
  box-shadow: none;
}
.input-group-text {
  background-color: transparent;
}
.table th,
.table td {
  vertical-align: middle;
  padding: 8px;
}
.table thead th {
  border-bottom: 2px solid #dee2e6;
  background-color: #f8f9fa;
}
.table-hover tbody tr:hover {
  background-color: #f1f1f1;
}
.parent-row {
  background-color: #f5f8fa;
  font-weight: 600;
  border-left: 4px solid #2c3f5e;
}
.child-row {
  background-color: #fafdff;
  border-left: 4px solid #b3c6e0;
}
.child-row .child-connector {
  display: inline-block;
  width: 12px;
  height: 16px;
  border-left: 2px solid #b3c6e0;
  margin-right: 4px;
  position: relative;
  top: 3px;
}
.toggle-icon {
  color: #2c3f5e;
  font-size: 1em;
}
.pagination {
  margin-top: 20px;
}
.page-link {
  color: #0b0a21;
  border: 1px solid #dde3eb;
  margin: 0 5px;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
}
.page-link:hover {
  background-color: #dde3eb;
}
.page-item.active .page-link {
  background-color: #2c3f5e;
  color: white;
  border-color: #2c3f5e;
}
.page-item.disabled .page-link {
  color: #6c757d;
  cursor: not-allowed;
  background-color: #f8f9fa;
  border-color: #dee2e6;
}
.pagination-container {
  gap: 15px;
}
.pagination-wrapper {
  flex: 1;
  max-width: 40%;
}
.pagination {
  margin-bottom: 0;
  flex-wrap: nowrap;
}
.page-link {
  padding: 0.375rem 0.75rem;
}
</style>